import { useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { headcountAtMonth } from '@/domain/headcount';
import type { AcquisitionModel } from '@/domain/types';
import { formatEur, formatNumber, formatQuantity } from '@/lib/format';
import { getTierColor } from '@/lib/tierColors';
import { useSimulatorState } from '@/store/useSimulatorState';

const MODEL_LABELS: Record<AcquisitionModel, string> = {
  's-curve': 'montée lente (courbe en S)',
  'linear-ramp': 'rampe linéaire',
  constant: 'constant',
};

function formatPercent(rate: number, decimals = 1): string {
  return `${formatNumber(rate * 100, decimals)} %`;
}

/**
 * Descriptif textuel de la configuration, affiché dans la vue générale pour
 * aider à la lecture des graphiques et fournir un contexte lisible à l'impression
 * PDF. Purement en lecture — ne modifie jamais l'état.
 */
export function ConfigSummary() {
  const team = useSimulatorState((s) => s.team);
  const recurringCosts = useSimulatorState((s) => s.recurringCosts);
  const fixedCosts = useSimulatorState((s) => s.fixedCosts);
  const categories = useSimulatorState((s) => s.categories);
  const tiers = useSimulatorState((s) => s.tiers);
  const initialDeficit = useSimulatorState((s) => s.initialDeficit);
  const hasEntryPrice = tiers.some((t) => (t.entryPrice ?? 0) > 0 || (t.entryCost ?? 0) > 0);
  const hasSupportCost = tiers.some((t) => (t.costPerClientPerMonth ?? 0) > 0);
  const acquisition = useSimulatorState((s) => s.acquisition);
  const horizonMonths = useSimulatorState((s) => s.projection.horizonMonths);

  const chargesMultiplier = team.chargesMultiplier ?? 1;
  const teamMonthlyCost = (headcount: number) =>
    (headcount * team.avgAnnualCostPerFTE * chargesMultiplier) / 12;
  const teamInitialCost = teamMonthlyCost(team.headcount);
  const recurringMonthlyCost = recurringCosts.reduce((sum, c) => sum + c.monthlyAmount, 0);
  const monthlyCostInitial = teamInitialCost + recurringMonthlyCost;
  const monthlyCostCruise =
    teamMonthlyCost(team.headcountRamp?.targetHeadcount ?? team.headcount) + recurringMonthlyCost;
  const totalFixed = fixedCosts.reduce((sum, c) => sum + c.amount, 0);

  const model = acquisition.model ?? 'constant';
  const rampUpMonths = acquisition.rampUpMonths ?? 0;
  const rate = acquisition.newClientsPerMonth;
  // Un rythme fractionnaire (ex. 0,5) doit rester visible ; un rythme entier reste « 10 ».
  const rateDecimals = Number.isInteger(rate) ? 0 : 2;
  const rampEnabled = team.headcountRamp?.enabled === true;
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? '—';

  const headcountRampSentence = useMemo(() => {
    if (!rampEnabled) return null;
    const target = team.headcountRamp?.targetHeadcount ?? team.headcount;
    if (target === team.headcount) {
      return `avec un effectif maintenu à ${formatQuantity(team.headcount)} ETP (cible identique au départ)`;
    }
    if (model === 'constant' || rampUpMonths <= 0) {
      return `avec un effectif de croisière fixé à ${formatQuantity(target)} ETP (montée immédiate, pas de courbe paramétrée)`;
    }
    const atMonth1 = headcountAtMonth(1, { team, acquisition });
    const atRampEnd = headcountAtMonth(rampUpMonths, { team, acquisition });
    return `avec un effectif qui passe de ${formatQuantity(team.headcount)} à ${formatQuantity(
      target,
    )} ETP selon la même courbe, soit ${formatQuantity(atMonth1)} ETP au mois 1 et ${formatQuantity(
      atRampEnd,
    )} ETP au mois ${formatNumber(rampUpMonths)}`;
  }, [rampEnabled, team, acquisition, model, rampUpMonths]);

  const discrete = acquisition.discrete === true;
  const salesCycleMonths = acquisition.salesCycleMonths ?? 0;
  const winRate = acquisition.winRate ?? 1;

  const baseAcquisitionSentence =
    model === 'constant'
      ? `un rythme constant de ${formatNumber(rate, rateDecimals)} nouveau(x) client(s) par mois dès le premier mois`
      : `un modèle « ${MODEL_LABELS[model]} » qui atteint ${formatNumber(
          rate,
          rateDecimals,
        )} nouveau(x) client(s) par mois après ${formatNumber(rampUpMonths)} mois de montée en charge`;

  // Le mode B2G (deals entiers) est décrit dans une phrase à part pour ne pas
  // surcharger la phrase d'acquisition principale.
  const presalesCostPerDeal = acquisition.presalesCostPerDeal ?? 0;
  const discreteSentence = discrete
    ? `Les clients sont signés en deals entiers (mode événementiel) : cycle de vente de ${formatNumber(
        salesCycleMonths,
      )} mois entre la signature et la première facturation, taux de win de ${formatPercent(
        winRate,
        0,
      )}${
        presalesCostPerDeal > 0
          ? `, coût d'avant-vente de ${formatEur(presalesCostPerDeal)} par dossier`
          : ''
      }.`
    : null;

  return (
    <Card className="break-inside-avoid gap-4">
      <CardHeader>
        <CardTitle className="text-base">Synthèse du scénario</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5 text-sm">
        <div className="flex flex-col gap-2 leading-relaxed text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Projection.</span> Horizon de{' '}
            <strong className="text-foreground">{horizonMonths} mois</strong>. L&apos;acquisition
            suit {baseAcquisitionSentence}.
          </p>
          {discreteSentence && (
            <p>
              <span className="font-medium text-foreground">Acquisition B2G.</span>{' '}
              {discreteSentence}
            </p>
          )}
          <p>
            <span className="font-medium text-foreground">Équipe &amp; coûts.</span> L&apos;équipe
            compte <strong className="text-foreground">{formatQuantity(team.headcount)} ETP</strong>
            {headcountRampSentence ? (
              <>
                {' '}
                {headcountRampSentence}, pour un coût mensuel récurrent qui évolue de{' '}
                <strong className="text-foreground">{formatEur(monthlyCostInitial)}</strong> à{' '}
                <strong className="text-foreground">{formatEur(monthlyCostCruise)}</strong>
              </>
            ) : (
              <>
                {' '}
                pour un coût mensuel récurrent de{' '}
                <strong className="text-foreground">{formatEur(monthlyCostInitial)}</strong>
              </>
            )}
            .
            {totalFixed > 0 && (
              <>
                {' '}
                S&apos;y ajoutent{' '}
                <strong className="text-foreground">{formatEur(totalFixed)}</strong> de coûts fixes
                ponctuels.
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {/* Équipe & coûts */}
          <section className="flex flex-col gap-1.5">
            <h3 className="font-semibold">Équipe &amp; coûts</h3>
            <dl className="flex flex-col gap-1 text-muted-foreground">
              <div className="flex justify-between gap-4">
                <dt>Effectif initial</dt>
                <dd className="text-foreground">{formatQuantity(team.headcount)} ETP</dd>
              </div>
              {rampEnabled && (
                <div className="flex justify-between gap-4">
                  <dt>Effectif cible (plateau)</dt>
                  <dd className="text-foreground">
                    {formatQuantity(team.headcountRamp?.targetHeadcount ?? team.headcount)} ETP
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt>Coût moyen / ETP / an</dt>
                <dd className="text-foreground">{formatEur(team.avgAnnualCostPerFTE)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Multiplicateur de charges</dt>
                <dd className="text-foreground">×{formatNumber(chargesMultiplier, 2)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Coût mensuel récurrent</dt>
                <dd className="font-medium text-foreground">
                  {rampEnabled
                    ? `${formatEur(monthlyCostInitial)} → ${formatEur(monthlyCostCruise)}`
                    : formatEur(monthlyCostInitial)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Coûts fixes ponctuels</dt>
                <dd className="font-medium text-foreground">{formatEur(totalFixed)}</dd>
              </div>
              {(initialDeficit ?? 0) > 0 && (
                <div className="flex justify-between gap-4">
                  <dt>Déficit initial</dt>
                  <dd className="font-medium text-foreground">{formatEur(initialDeficit ?? 0)}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Acquisition & projection */}
          <section className="flex flex-col gap-1.5">
            <h3 className="font-semibold">Acquisition &amp; projection</h3>
            <dl className="flex flex-col gap-1 text-muted-foreground">
              <div className="flex justify-between gap-4">
                <dt>Modèle</dt>
                <dd className="text-right text-foreground">{MODEL_LABELS[model]}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>{model === 'constant' ? 'Rythme' : 'Rythme de croisière'}</dt>
                <dd className="text-foreground">{formatNumber(rate, rateDecimals)} clients/mois</dd>
              </div>
              {model !== 'constant' && (
                <div className="flex justify-between gap-4">
                  <dt>Montée en charge</dt>
                  <dd className="text-foreground">{formatNumber(rampUpMonths)} mois</dd>
                </div>
              )}
              {discrete && (
                <>
                  <div className="flex justify-between gap-4">
                    <dt>Mode</dt>
                    <dd className="text-right text-foreground">deals entiers (B2G)</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt>Cycle de vente</dt>
                    <dd className="text-foreground">{formatNumber(salesCycleMonths)} mois</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt>Taux de win</dt>
                    <dd className="text-foreground">{formatPercent(winRate, 0)}</dd>
                  </div>
                  {(acquisition.presalesCostPerDeal ?? 0) > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt>Coût avant-vente / dossier</dt>
                      <dd className="text-foreground">
                        {formatEur(acquisition.presalesCostPerDeal ?? 0)}
                      </dd>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between gap-4">
                <dt>Horizon</dt>
                <dd className="text-foreground">{horizonMonths} mois</dd>
              </div>
            </dl>
          </section>
        </div>

        {/* Coûts récurrents (détail par poste) */}
        {recurringCosts.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="font-semibold">Coûts récurrents (mensuels)</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[320px] border-collapse text-left">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="py-1.5 pr-4 font-medium">Poste</th>
                    <th className="py-1.5 pr-4 font-medium">Catégorie</th>
                    <th className="py-1.5 text-right font-medium">Montant / mois</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringCosts.map((cost) => (
                    <tr key={cost.id} className="border-b">
                      <td className="py-1.5 pr-4">{cost.label}</td>
                      <td className="py-1.5 pr-4 text-muted-foreground">
                        {categoryName(cost.categoryId)}
                      </td>
                      <td className="py-1.5 text-right">{formatEur(cost.monthlyAmount)}</td>
                    </tr>
                  ))}
                  <tr className="font-medium">
                    <td className="py-1.5 pr-4">Total</td>
                    <td className="py-1.5 pr-4" />
                    <td className="py-1.5 text-right">{formatEur(recurringMonthlyCost)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Paliers tarifaires */}
        {tiers.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="font-semibold">Paliers tarifaires</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[380px] border-collapse text-left">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="py-1.5 pr-4 font-medium">Palier</th>
                    <th className="py-1.5 pr-4 text-right font-medium">Prix / mois</th>
                    {hasEntryPrice && (
                      <>
                        <th className="py-1.5 pr-4 text-right font-medium">Prix d&apos;entrée</th>
                        <th className="py-1.5 pr-4 text-right font-medium">Coût unit.</th>
                      </>
                    )}
                    {hasSupportCost && (
                      <th className="py-1.5 pr-4 text-right font-medium">Support / client</th>
                    )}
                    <th className="py-1.5 pr-4 text-right font-medium">Churn mensuel</th>
                    <th className="py-1.5 text-right font-medium">Part d&apos;acquisition</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier, i) => (
                    <tr key={tier.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-4">
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="inline-block size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: getTierColor(i) }}
                          />
                          {tier.name}
                        </span>
                      </td>
                      <td className="py-1.5 pr-4 text-right">{formatEur(tier.monthlyPrice)}</td>
                      {hasEntryPrice && (
                        <>
                          <td className="py-1.5 pr-4 text-right">
                            {tier.entryPrice ? formatEur(tier.entryPrice) : '—'}
                          </td>
                          <td className="py-1.5 pr-4 text-right">
                            {tier.entryCost ? formatEur(tier.entryCost) : '—'}
                          </td>
                        </>
                      )}
                      {hasSupportCost && (
                        <td className="py-1.5 pr-4 text-right">
                          {tier.costPerClientPerMonth ? formatEur(tier.costPerClientPerMonth) : '—'}
                        </td>
                      )}
                      <td className="py-1.5 pr-4 text-right">
                        {formatPercent(tier.monthlyChurnRate)}
                      </td>
                      <td className="py-1.5 text-right">
                        {formatPercent(tier.acquisitionShare, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
